import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  IconButton,
} from '@mui/material';
import { Info, X } from '@phosphor-icons/react';
import { format, isPast } from 'date-fns';
import { useEffect } from 'react';
import { Api } from '../../../api';

type Props = {
  open: boolean;
  onClose: () => void;
  teams: Api.OrganisationTeam.V_OrganisationSub[];
  currentTeamId: string;
  selectedTeam: string;
  setSelectedTeam: (id: string) => void;
  disableSave: boolean;
  onSave: () => void | Promise<void>;
};

export default function MoveLicencesModal({
  open,
  onClose,
  teams,
  currentTeamId,
  selectedTeam,
  setSelectedTeam,
  disableSave,
  onSave,
}: Props) {
  useEffect(() => {
    if (!open) return;
    const otherTeams = teams.filter((t) => t.id !== currentTeamId);
    const availableTeams = otherTeams.filter(
      (t) => !t.programStartDate || !isPast(new Date(t.programStartDate)),
    );
    if (availableTeams.length === 1) setSelectedTeam(availableTeams[0]['@id']);
  }, [open, teams, currentTeamId, setSelectedTeam]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          Move to other team
          <IconButton onClick={onClose}>
            <X size={24} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack>
            <Typography variant="subtitle1">
              Which Team do you want to move the selected client(s) to?
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Info size={20} color="#667085" />
              <Typography variant="body1" color="text.secondary">
                You can only move users if the programs haven’t started yet.
              </Typography>
            </Stack>
          </Stack>
          <FormControl>
            <RadioGroup
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
            >
              {teams
                .filter((t) => t.id !== currentTeamId)
                .map((t) => {
                  const isProgramStarted = Boolean(
                    t.programStartDate && isPast(new Date(t.programStartDate)),
                  );
                  return (
                    <FormControlLabel
                      key={t.id}
                      value={t['@id']}
                      control={<Radio />}
                      disabled={isProgramStarted}
                      label={
                        <Stack>
                          <Typography variant="body2">{t.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Coach: {t.coach.name} | Program Start Date:{' '}
                            {t.programStartDate
                              ? format(
                                  new Date(t.programStartDate),
                                  'dd MMM yyyy',
                                )
                              : '-'}
                          </Typography>
                        </Stack>
                      }
                    />
                  );
                })}
            </RadioGroup>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button variant="contained" onClick={onSave} disabled={disableSave}>
          Save
        </Button>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
